import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MemoryIcon from '@mui/icons-material/Memory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { paymentQueueMonitorService } from '../services';

const stickyActionSx = {
  position: 'sticky' as const,
  right: 0,
  bgcolor: 'background.paper',
  zIndex: 2,
  boxShadow: (theme: { palette: { mode: string } }) =>
    theme.palette.mode === 'dark'
      ? '-4px 0 12px rgba(0,0,0,0.35)'
      : '-4px 0 10px rgba(0,0,0,0.08)',
};

const ellipsizedCellSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  maxWidth: 1,
};

const formatDate = (iso?: string | Date | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return String(iso);
  }
};

const formatDateCompact = (iso?: string | Date | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

const providerLabel = (provider?: string | null, label?: string | null) => {
  if (label && label !== '—') return label;
  if (provider === 'mercadopago') return 'Mercado Pago';
  if (provider === 'pagarme') return 'Pagar.me';
  return '—';
};

const statusColor = (s: string) => {
  if (s === 'paid') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'failed') return 'error';
  return 'default';
};

const splitChipColor = (applied: boolean | null, label: string) => {
  if (applied === true) return 'success';
  if (label.startsWith('Só contábil')) return 'info';
  if (applied === false) return 'warning';
  return 'default';
};

const canReconcileRegistration = (r: {
  paymentStatus: string;
  paymentId: string;
}) =>
  (r.paymentStatus === 'pending' || r.paymentStatus === 'failed') &&
  Boolean(r.paymentId) &&
  r.paymentId !== '—';

const shortId = (value?: string | null) => {
  if (!value || value === '—') return '—';
  if (value.length <= 14) return value;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
};

type SortMode = 'created_asc' | 'created_desc' | 'updated_asc' | 'updated_desc';

const SortModeControls: React.FC<{
  sortMode: SortMode;
  updatedColumnLabel?: string;
  onChange: (mode: SortMode) => void;
}> = ({ sortMode, updatedColumnLabel = 'Atualizado', onChange }) => {
  const updatedGroupLabel =
    updatedColumnLabel === 'Falhou em' ? 'Falhou em' : 'Atualizado';
  const field: 'created' | 'updated' = sortMode.startsWith('updated_') ? 'updated' : 'created';
  const dir: 'asc' | 'desc' = sortMode.endsWith('_asc') ? 'asc' : 'desc';

  const toggleSx = {
    bgcolor: 'background.paper',
    '& .MuiToggleButton-root': {
      px: 1.25,
      py: 0.35,
      textTransform: 'none',
      fontSize: 12,
      lineHeight: 1.2,
      borderColor: 'divider',
    },
    '& .Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.dark' },
    },
  } as const;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      gap={{ xs: 1, sm: 1.5 }}
      flexWrap="wrap"
      sx={{ flex: 1, minWidth: 0 }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={field}
        onChange={(_, value: 'created' | 'updated' | null) => {
          if (value) onChange(`${value}_${dir}` as SortMode);
        }}
        sx={toggleSx}
      >
        <ToggleButton value="created">Criado</ToggleButton>
        <ToggleButton value="updated">{updatedGroupLabel}</ToggleButton>
      </ToggleButtonGroup>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={dir}
        onChange={(_, value: 'asc' | 'desc' | null) => {
          if (value) onChange(`${field}_${value}` as SortMode);
        }}
        sx={toggleSx}
      >
        <ToggleButton value="asc">
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <ArrowUpwardIcon sx={{ fontSize: 15 }} />
            <Box component="span">Antigo</Box>
          </Stack>
        </ToggleButton>
        <ToggleButton value="desc">
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <ArrowDownwardIcon sx={{ fontSize: 15 }} />
            <Box component="span">Novo</Box>
          </Stack>
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    fontWeight={700}
    letterSpacing={0.8}
    sx={{ display: 'block', mb: 1 }}
  >
    {children}
  </Typography>
);

const MetricCard: React.FC<{
  icon: React.ReactNode;
  tone: 'primary' | 'secondary' | 'info';
  title: string;
  children: React.ReactNode;
}> = ({ icon, tone, title, children }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderRadius: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 2 },
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" mb={2}>
        <Avatar
          variant="rounded"
          sx={{
            width: 40,
            height: 40,
            bgcolor: (theme) => `${theme.palette[tone].main}18`,
            color: `${tone}.main`,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
          {title}
        </Typography>
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const StatLine: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} py={0.35}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">
      {value}
    </Typography>
  </Stack>
);

const parseSortTimestamp = (iso?: string | null) => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
};

const sortRows = (items: RegistrationRow[], sortMode: SortMode) => {
  const copy = [...items];
  const compareIds = (a: RegistrationRow, b: RegistrationRow) => a.id.localeCompare(b.id);

  copy.sort((a, b) => {
    let diff = 0;
    switch (sortMode) {
      case 'created_asc':
        diff = parseSortTimestamp(a.createdAt) - parseSortTimestamp(b.createdAt);
        break;
      case 'created_desc':
        diff = parseSortTimestamp(b.createdAt) - parseSortTimestamp(a.createdAt);
        break;
      case 'updated_asc':
        diff = parseSortTimestamp(a.updatedAt) - parseSortTimestamp(b.updatedAt);
        break;
      case 'updated_desc':
        diff = parseSortTimestamp(b.updatedAt) - parseSortTimestamp(a.updatedAt);
        break;
      default:
        break;
    }
    return diff !== 0 ? diff : compareIds(a, b);
  });

  return copy;
};

type RegistrationRow = {
  id: string;
  name?: string;
  email?: string;
  eventTitle?: string;
  paymentStatus: string;
  paymentProvider?: string | null;
  paymentProviderLabel?: string | null;
  splitApplied?: boolean | null;
  splitDetail?: string | null;
  splitLabel?: string | null;
  paymentMethod?: string | null;
  paymentId: string;
  confirmationCode?: string | null;
  createdAt: string;
  updatedAt: string;
};

type RegistrationsPanelProps = {
  title: string;
  subtitle: React.ReactNode;
  statusChips?: React.ReactNode;
  rows: RegistrationRow[];
  page: number;
  rowsPerPage: number;
  sortMode: SortMode;
  eventFilter: string;
  eventOptions: string[];
  updatedColumnLabel?: string;
  isNarrow: boolean;
  reconcilingId: string | null;
  reconcilePending: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onSortModeChange: (mode: SortMode) => void;
  onEventFilterChange: (value: string) => void;
  onReconcile: (id: string) => void;
  filterLabelId?: string;
};

const RegistrationsPanel: React.FC<RegistrationsPanelProps> = ({
  title,
  subtitle,
  statusChips,
  rows,
  page,
  rowsPerPage,
  sortMode,
  eventFilter,
  eventOptions,
  updatedColumnLabel = 'Atualizado',
  isNarrow,
  reconcilingId,
  reconcilePending,
  onPageChange,
  onRowsPerPageChange,
  onSortModeChange,
  onEventFilterChange,
  onReconcile,
  filterLabelId = 'event-filter-label',
}) => {
  const filtered = useMemo(() => {
    if (eventFilter === 'all') return rows;
    return rows.filter((r) => String(r.eventTitle || '—') === eventFilter);
  }, [rows, eventFilter]);

  const sorted = useMemo(
    () => sortRows(filtered, sortMode),
    [filtered, sortMode]
  );

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sorted]);

  const handleSortHeader = (field: 'created' | 'updated') => {
    const ascMode = `${field}_asc` as SortMode;
    const descMode = `${field}_desc` as SortMode;
    if (sortMode === descMode) {
      onSortModeChange(ascMode);
    } else {
      onSortModeChange(descMode);
    }
    onPageChange(0);
  };

  const createdDirection = sortMode === 'created_asc' ? 'asc' : sortMode === 'created_desc' ? 'desc' : false;
  const updatedDirection = sortMode === 'updated_asc' ? 'asc' : sortMode === 'updated_desc' ? 'desc' : false;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        mb: 1.5,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ pb: rows.length > 0 ? 1.25 : 0.75, mb: rows.length > 0 ? 1.25 : 0, borderBottom: rows.length > 0 ? 1 : 0, borderColor: 'divider' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={0.75}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              {subtitle}
            </Typography>
          </Box>
          <Chip size="small" variant="outlined" label={`${sorted.length} registro(s)`} sx={{ flexShrink: 0 }} />
        </Stack>
        {statusChips ? (
          <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
            {statusChips}
          </Stack>
        ) : null}
      </Box>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }}>
          Nenhuma inscrição no período selecionado.
        </Typography>
      ) : (
        <>
      <Box
        sx={{
          p: { xs: 1, sm: 1.25 },
          mb: 1.25,
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1.5, md: 2 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
              Evento
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id={filterLabelId}>Filtrar por evento</InputLabel>
              <Select
                labelId={filterLabelId}
                value={eventFilter}
                label="Filtrar por evento"
                onChange={(e) => onEventFilterChange(e.target.value)}
              >
                <MenuItem value="all">Todos os eventos</MenuItem>
                {eventOptions.map((eventName) => (
                  <MenuItem key={eventName} value={eventName}>
                    {eventName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              width: '1px',
              alignSelf: 'stretch',
              bgcolor: 'divider',
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
              Ordenar por
            </Typography>
            <SortModeControls
              sortMode={sortMode}
              updatedColumnLabel={updatedColumnLabel}
              onChange={onSortModeChange}
            />
          </Box>
        </Stack>
      </Box>
      <TableContainer
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            minWidth: 1000,
            '& .MuiTableCell-root': { py: 0.75, px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell width="9%" sortDirection={createdDirection || false}>
                <TableSortLabel
                  active={sortMode.startsWith('created_')}
                  direction={createdDirection || 'desc'}
                  onClick={() => handleSortHeader('created')}
                >
                  Criado
                </TableSortLabel>
              </TableCell>
              <TableCell width="9%" sortDirection={updatedDirection || false}>
                <TableSortLabel
                  active={sortMode.startsWith('updated_')}
                  direction={updatedDirection || 'desc'}
                  onClick={() => handleSortHeader('updated')}
                >
                  {updatedColumnLabel}
                </TableSortLabel>
              </TableCell>
              <TableCell width="10%">Nome</TableCell>
              <TableCell width="12%">Email</TableCell>
              <TableCell width="14%">Evento</TableCell>
              <TableCell width="7%">Status</TableCell>
              <TableCell width="9%">Provedor</TableCell>
              <TableCell width="8%">Split</TableCell>
              <TableCell width="6%">Método</TableCell>
              <TableCell width="10%">ID pagamento</TableCell>
              <TableCell width="9%">Código</TableCell>
              <TableCell align="right" width="10%" sx={stickyActionSx}>
                Ação
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody key={sortMode}>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
                    Nenhuma inscrição para os filtros selecionados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paged.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={ellipsizedCellSx}>
                    <Tooltip title={formatDate(r.createdAt)}>
                      <Typography variant="caption" component="span" noWrap display="block">
                        {formatDateCompact(r.createdAt)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={ellipsizedCellSx}>
                    <Tooltip title={formatDate(r.updatedAt)}>
                      <Typography variant="caption" component="span" noWrap display="block">
                        {formatDateCompact(r.updatedAt)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={ellipsizedCellSx}>
                    <Tooltip title={r.name || ''}>
                      <span>{r.name || '—'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={ellipsizedCellSx}>
                    <Tooltip title={r.email || ''}>
                      <span>{r.email || '—'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={ellipsizedCellSx}>
                    <Tooltip title={r.eventTitle || ''}>
                      <span>{r.eventTitle || '—'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.paymentStatus}
                      color={
                        statusColor(r.paymentStatus) as
                          | 'success'
                          | 'warning'
                          | 'error'
                          | 'default'
                      }
                    />
                  </TableCell>
                  <TableCell sx={ellipsizedCellSx}>
                    <Tooltip title={providerLabel(r.paymentProvider, r.paymentProviderLabel)}>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={
                          r.paymentProvider === 'pagarme'
                            ? 'primary.main'
                            : r.paymentProvider === 'mercadopago'
                              ? 'secondary.main'
                              : 'text.secondary'
                        }
                        noWrap
                        display="block"
                      >
                        {providerLabel(r.paymentProvider, r.paymentProviderLabel)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.splitDetail || r.splitLabel || ''}>
                      <Chip
                        size="small"
                        label={r.splitLabel || '—'}
                        color={
                          splitChipColor(r.splitApplied ?? null, r.splitLabel || '') as
                            | 'success'
                            | 'warning'
                            | 'info'
                            | 'default'
                        }
                        sx={{ ...ellipsizedCellSx, maxWidth: '100%' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={ellipsizedCellSx}>{r.paymentMethod || '—'}</TableCell>
                  <TableCell>
                    <CopyableId value={r.paymentId} />
                  </TableCell>
                  <TableCell>
                    <CopyableId value={r.confirmationCode} />
                  </TableCell>
                  <TableCell align="right" sx={stickyActionSx}>
                    {canReconcileRegistration(r) ? (
                      <Tooltip title="Consultar Mercado Pago / Pagar.me e confirmar se já estiver pago">
                        <span>
                          {isNarrow ? (
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={reconcilePending}
                              onClick={() => onReconcile(r.id)}
                              aria-label="Verificar no provedor"
                            >
                              {reconcilingId === r.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <SyncIcon fontSize="small" />
                              )}
                            </IconButton>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                reconcilingId === r.id ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <SyncIcon fontSize="small" />
                                )
                              }
                              disabled={reconcilePending}
                              onClick={() => onReconcile(r.id)}
                              sx={{ whiteSpace: 'nowrap', minWidth: 0, px: 1 }}
                            >
                              Verificar
                            </Button>
                          )}
                        </span>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(_, nextPage) => onPageChange(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          onRowsPerPageChange(parseInt(event.target.value, 10));
          onPageChange(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Linhas por página"
      />
        </>
      )}
    </Paper>
  );
};

const CopyableId: React.FC<{ value?: string | null; monospace?: boolean }> = ({
  value,
  monospace = true,
}) => {
  if (!value || value === '—') {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
      <Tooltip title={value}>
        <Typography
          variant="body2"
          component="span"
          sx={{
            ...ellipsizedCellSx,
            fontFamily: monospace ? 'monospace' : 'inherit',
            fontSize: 12,
            flex: 1,
            minWidth: 0,
          }}
        >
          {shortId(value)}
        </Typography>
      </Tooltip>
      <Tooltip title="Copiar">
        <IconButton
          size="small"
          aria-label="Copiar"
          onClick={() => navigator.clipboard.writeText(value)}
          sx={{ flexShrink: 0, p: 0.25 }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const PaymentQueues: React.FC = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('lg'));
  const queryClient = useQueryClient();
  const [hours, setHours] = useState(24);
  const [mainTab, setMainTab] = useState(0);
  const [page, setPage] = useState(0);
  const [failedPage, setFailedPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [failedRowsPerPage, setFailedRowsPerPage] = useState(20);
  const [sortMode, setSortMode] = useState<SortMode>('created_desc');
  const [failedSortMode, setFailedSortMode] = useState<SortMode>('updated_desc');
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [failedEventFilter, setFailedEventFilter] = useState<string>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['payment-queues-overview', hours, customFrom, customTo],
    queryFn: async () => {
      const timeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('A consulta demorou demais. Verifique se a API e o Redis estão ativos.')), 30_000);
      });
      return Promise.race([
        paymentQueueMonitorService.getOverview({
          hours,
          from: customFrom || undefined,
          to: customTo || undefined,
        }),
        timeout,
      ]);
    },
    refetchInterval: 15_000,
    retry: 1,
  });

  const reconcileMutation = useMutation({
    mutationFn: (registrationId: string) =>
      paymentQueueMonitorService.reconcileRegistration(registrationId),
    onMutate: (registrationId) => {
      setReconcilingId(registrationId);
    },
    onSettled: () => {
      setReconcilingId(null);
    },
    onSuccess: (res) => {
      const result = res?.data;
      const severity =
        result?.outcome === 'confirmed' || result?.outcome === 'already_confirmed'
          ? 'success'
          : result?.outcome === 'still_pending'
            ? 'info'
            : 'error';
      setSnackbar({
        open: true,
        message: result?.message || res?.message || 'Verificação concluída',
        severity,
      });
      queryClient.invalidateQueries({ queryKey: ['payment-queues-overview'] });
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Não foi possível verificar o pagamento agora',
        severity: 'error',
      });
    },
  });

  const overview = data?.data;

  const workerStats = overview?.worker?.stats as Record<string, unknown> | undefined;

  const statusChips = useMemo(() => {
    const by = overview?.summary?.byPaymentStatus || {};
    return Object.entries(by)
      .filter(([k]) => k !== 'failed')
      .map(([k, v]) => (
        <Chip
          key={k}
          label={`${k}: ${v}`}
          color={statusColor(k) as 'success' | 'warning' | 'error' | 'default'}
          size="small"
        />
      ));
  }, [overview]);

  const periodLabel = customFrom || customTo
    ? 'Personalizado'
    : `Últimas ${hours}h`;

  const activeRegistrations = useMemo(
    () => (overview?.registrations || []).filter((r) => r.paymentStatus !== 'failed'),
    [overview?.registrations]
  );

  const failedRegistrations = useMemo(
    () => overview?.failedRegistrations || [],
    [overview?.failedRegistrations]
  );

  const activeEventOptions = useMemo(() => {
    const options = Array.from(
      new Set(activeRegistrations.map((r) => String(r.eventTitle || '—')))
    ).sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
    return options;
  }, [activeRegistrations]);

  const failedEventOptions = useMemo(() => {
    const options = Array.from(
      new Set(failedRegistrations.map((r) => String(r.eventTitle || '—')))
    ).sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
    return options;
  }, [failedRegistrations]);

  const failedCount = overview?.summary?.failedRegistrationsCount ?? failedRegistrations.length;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, mb: 1.5, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
          mb={1}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            PERÍODO DE ANÁLISE
          </Typography>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton
                onClick={() => refetch()}
                disabled={isFetching}
                color="primary"
                size="small"
                sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
              >
                {isFetching ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={customFrom || customTo ? '' : hours}
            onChange={(_, value: number | null) => {
              if (value) {
                setHours(value);
                setCustomFrom('');
                setCustomTo('');
              }
            }}
            sx={{ flexShrink: 0 }}
          >
            {[12, 24, 48].map((h) => (
              <ToggleButton key={h} value={h} sx={{ px: 2, textTransform: 'none' }}>
                {h}h
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', lg: 'block' } }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1, maxWidth: { lg: 560 } }}>
            <TextField
              size="small"
              type="datetime-local"
              label="Início"
              InputLabelProps={{ shrink: true }}
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label="Fim"
              InputLabelProps={{ shrink: true }}
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            />
            {(customFrom || customTo) && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setCustomFrom('');
                  setCustomTo('');
                }}
                sx={{ flexShrink: 0, alignSelf: { sm: 'center' } }}
              >
                Limpar
              </Button>
            )}
          </Stack>
        </Stack>
        {overview && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
            <Chip size="small" label={periodLabel} color="primary" variant="outlined" />
            <Chip
              size="small"
              variant="outlined"
              label={`${formatDate(overview.windowFrom || null)} → ${formatDate(overview.windowTo || null)}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Atualizado ${formatDateCompact(overview.generatedAt)}`}
            />
          </Stack>
        )}
      </Paper>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error)?.message ||
            'Não foi possível carregar o monitor. Verifique se a API e o Redis estão ativos.'}
        </Alert>
      )}

      {overview && (
        <>
          <Tabs
            value={mainTab}
            onChange={(_, value) => setMainTab(value)}
            sx={{
              mb: 1.5,
              minHeight: 40,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.75 },
            }}
          >
            <Tab label="Monitor da fila" />
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>Failed / expirados</span>
                  {failedCount > 0 ? (
                    <Chip label={failedCount} size="small" color="error" sx={{ height: 22 }} />
                  ) : null}
                </Stack>
              }
            />
          </Tabs>

          {mainTab === 0 && (
            <>
              <RegistrationsPanel
                title={`Criadas no período (${periodLabel.toLowerCase()})`}
                subtitle="Inscrições pending e paid criadas no intervalo. Use Verificar para consultar o provedor."
                statusChips={
                  statusChips.length ? statusChips : <Typography variant="body2">Nenhuma no período</Typography>
                }
                rows={activeRegistrations}
                page={page}
                rowsPerPage={rowsPerPage}
                sortMode={sortMode}
                eventFilter={eventFilter}
                eventOptions={activeEventOptions}
                updatedColumnLabel="Atualizado"
                isNarrow={isNarrow}
                reconcilingId={reconcilingId}
                reconcilePending={reconcileMutation.isPending}
                onPageChange={setPage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(value);
                  setPage(0);
                }}
                onSortModeChange={(mode) => {
                  setSortMode(mode);
                  setPage(0);
                }}
                onEventFilterChange={(value) => {
                  setEventFilter(value);
                  setPage(0);
                }}
                onReconcile={(id) => reconcileMutation.mutate(id)}
                filterLabelId="event-filter-active"
              />

              <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, mb: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Eventos na fila de reconciliação
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  Mensagens publicadas na stream Redis no período selecionado.
                </Typography>
                <TableContainer
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell width="22%">Quando</TableCell>
                        <TableCell width="12%">PSP</TableCell>
                        <TableCell width="22%">ID pagamento</TableCell>
                        <TableCell width="22%">Inscrição</TableCell>
                        <TableCell width="22%">Stream ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overview.streamEvents.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary">
                              Nenhum evento na stream no período (ou Redis/fila desligados).
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        overview.streamEvents.slice(0, 100).map((ev) => (
                          <TableRow key={ev.id} hover>
                            <TableCell sx={ellipsizedCellSx}>{formatDate(ev.occurredAt)}</TableCell>
                            <TableCell>{ev.provider || '—'}</TableCell>
                            <TableCell>
                              <CopyableId value={ev.providerPaymentId} />
                            </TableCell>
                            <TableCell>
                              <CopyableId value={ev.registrationId} />
                            </TableCell>
                            <TableCell>
                              <CopyableId value={ev.id} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <SectionTitle>Infraestrutura</SectionTitle>
              <Grid container spacing={1.5} mb={2}>
                <Grid item xs={12} md={4}>
                  <MetricCard icon={<MemoryIcon fontSize="small" />} tone="primary" title="Fila Redis">
                    {overview.redis?.error ? (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        {overview.redis.error}
                      </Alert>
                    ) : null}
                    <Stack spacing={0.25} divider={<Divider flexItem />}>
                      <StatLine
                        label="Stream"
                        value={
                          <Typography component="code" variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {overview.redis?.streamKey || '—'}
                          </Typography>
                        }
                      />
                      <StatLine
                        label="Fila habilitada"
                        value={overview.redis?.queueEnabled ? 'Sim' : 'Não'}
                      />
                      <StatLine label="Mensagens" value={overview.redis?.length ?? '—'} />
                      <StatLine label="Pendentes no grupo" value={overview.redis?.pendingInGroup ?? '—'} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                      {overview.summary.streamEventsCount} evento(s) publicados no período
                    </Typography>
                  </MetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MetricCard icon={<AccountBalanceIcon fontSize="small" />} tone="secondary" title="Worker Go">
                    {overview.worker.reachable ? (
                      <>
                        <Chip
                          size="small"
                          label={`Último tick: ${String(workerStats?.lastTickStatus ?? '—')}`}
                          color={
                            workerStats?.lastTickStatus === 'ok'
                              ? 'success'
                              : workerStats?.lastTickStatus === 'error'
                                ? 'error'
                                : 'warning'
                          }
                          sx={{ mb: 1.5 }}
                        />
                        <Stack spacing={0.25} divider={<Divider flexItem />}>
                          <StatLine label="Processados" value={String(workerStats?.totalProcessed ?? 0)} />
                          <StatLine label="Aprovados" value={String(workerStats?.totalApproved ?? 0)} />
                          <StatLine label="Falhas" value={String(workerStats?.totalFailed ?? 0)} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                          {formatDate(workerStats?.lastTickAt as string)}
                        </Typography>
                        {workerStats?.lastError ? (
                          <Alert severity="error" sx={{ mt: 1.5 }}>
                            {String(workerStats.lastError)}
                          </Alert>
                        ) : null}
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          href={overview.worker.dashboardUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ mt: 1.5 }}
                        >
                          Dashboard do worker
                        </Button>
                      </>
                    ) : (
                      <Alert severity="info">
                        Worker inacessível. Suba <code>worker-go-payments-dev</code> (porta 8088).
                      </Alert>
                    )}
                  </MetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MetricCard icon={<EmailOutlinedIcon fontSize="small" />} tone="info" title="Fila de e-mails">
                    {'error' in (overview.emailQueue || {}) && overview.emailQueue?.error ? (
                      <Alert severity="info">{overview.emailQueue.error}</Alert>
                    ) : (
                      <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        <Chip size="small" variant="outlined" label={`Aguardando ${overview.emailQueue.waiting}`} />
                        <Chip size="small" variant="outlined" label={`Ativo ${overview.emailQueue.active}`} />
                        <Chip size="small" color="success" label={`Ok ${overview.emailQueue.completed}`} />
                        <Chip size="small" color="error" label={`Falha ${overview.emailQueue.failed}`} />
                      </Stack>
                    )}
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      href={`${(process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api\/?$/, '')}/queues`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mt: 1.5 }}
                    >
                      Bull Board
                    </Button>
                  </MetricCard>
                </Grid>
              </Grid>
            </>
          )}

          {mainTab === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }} icon={false}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  O que aparece aqui
                </Typography>
                <Typography variant="body2">
                  Pagamentos que ficaram pending e expiraram no Mercado Pago (PIX ~24h). A coluna{' '}
                  <strong>Falhou em</strong> é quando o worker marcou como failed — não é a data de criação.
                </Typography>
              </Alert>
              <RegistrationsPanel
                title={`Failed / expirados (${failedCount})`}
                subtitle={`Marcados como failed entre ${formatDate(overview.windowFrom || null)} e ${formatDate(overview.windowTo || null)}.`}
                rows={failedRegistrations}
                page={failedPage}
                rowsPerPage={failedRowsPerPage}
                sortMode={failedSortMode}
                eventFilter={failedEventFilter}
                eventOptions={failedEventOptions}
                updatedColumnLabel="Falhou em"
                isNarrow={isNarrow}
                reconcilingId={reconcilingId}
                reconcilePending={reconcileMutation.isPending}
                onPageChange={setFailedPage}
                onRowsPerPageChange={(value) => {
                  setFailedRowsPerPage(value);
                  setFailedPage(0);
                }}
                onSortModeChange={(mode) => {
                  setFailedSortMode(mode);
                  setFailedPage(0);
                }}
                onEventFilterChange={(value) => {
                  setFailedEventFilter(value);
                  setFailedPage(0);
                }}
                onReconcile={(id) => reconcileMutation.mutate(id)}
                filterLabelId="event-filter-failed"
              />
            </>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentQueues;
